import type { ClipboardEvent } from 'react';
import { joinedToChips } from './formFields/joinedChips';

/**
 * Copy and paste for a chips field — the multi-selects of the forms and the
 * grid cells.
 *
 * The chips are not text, so the browser has nothing to copy from them. Ctrl+C
 * in the field with no text selected puts the chips on the clipboard as one
 * line, "Bef. 12, Bef. 11" — the same spelling a grid cell copies as (see
 * withListSeparator). Pasting such a line back turns it into chips again.
 *
 * Written through `clipboardData`, not `navigator.clipboard`: the field app is
 * routinely opened outside a secure context, where the latter does not exist.
 */
export interface ChipClipboard<Option> {
    /** What the field holds now, in chip order. */
    value: readonly Option[];
    /** The chip's text: what a copy carries and a paste is matched by. */
    getLabel: (option: Option) => string;
    /** The offered entries; a pasted entry is matched by label, any case. */
    options: readonly Option[];
    /**
     * A pasted entry no option names, as an entry of its own. Absent, or
     * returning null, drops it: a closed vocabulary takes nothing else.
     */
    fromText?: (text: string) => Option | null;
    /** An option the list greys out is not pasted in either. */
    isOptionDisabled?: (option: Option) => boolean;
    onChange: (next: Option[]) => void;
    readOnly?: boolean;
}

const LIST_SEPARATOR = ', ';

/** The chips as the one line a copy carries. */
export function chipsToClipboardText<Option>(
    value: readonly Option[],
    getLabel: (option: Option) => string
): string {
    return value.map((option) => getLabel(option)).join(LIST_SEPARATOR);
}

/**
 * The field's new value after `text` is pasted — or null where the paste is
 * better left to the browser, as text in the input.
 *
 * That is the case for ONE entry no option names exactly: typed into the input
 * it filters the list, which is what pasting a fragment ("Bef. 7") is for. A
 * list, or one exact name, becomes chips; what is already in stays where it is.
 * Lines and tabs separate like commas, so a column copied from Excel pastes too.
 */
export function pastedChips<Option>(
    text: string,
    {
        value,
        getLabel,
        options,
        fromText,
        isOptionDisabled
    }: Pick<
        ChipClipboard<Option>,
        'value' | 'getLabel' | 'options' | 'fromText' | 'isOptionDisabled'
    >
): Option[] | null {
    const optionByLabel = new Map<string, Option>();
    // Kept apart so free text cannot bring a greyed-out option back in.
    const disabledLabels = new Set<string>();
    for (const option of options) {
        const label = getLabel(option).toLowerCase();
        if (isOptionDisabled?.(option)) {
            disabledLabels.add(label);
        } else if (!optionByLabel.has(label)) {
            optionByLabel.set(label, option);
        }
    }
    const entries = joinedToChips(
        text.replace(/[\r\n\t]+/g, ','),
        options.map((option) => getLabel(option))
    );
    const oneExactName =
        entries.length === 1 && optionByLabel.has(entries[0].toLowerCase());
    if (entries.length < 2 && !oneExactName) {
        return null;
    }

    const next = [...value];
    const taken = new Set(
        value.map((option) => getLabel(option).toLowerCase())
    );
    let recognised = false;
    for (const entry of entries) {
        if (disabledLabels.has(entry.toLowerCase())) {
            continue;
        }
        const option =
            optionByLabel.get(entry.toLowerCase()) ?? fromText?.(entry) ?? null;
        if (option === null) {
            continue;
        }
        recognised = true;
        const label = getLabel(option).toLowerCase();
        if (!taken.has(label)) {
            taken.add(label);
            next.push(option);
        }
    }
    // Nothing recognised: the text itself is more use than an empty paste.
    return recognised ? next : null;
}

/** Whether the user has marked text of their own to copy. */
function hasTextSelection(target: EventTarget): boolean {
    if (
        target instanceof HTMLInputElement &&
        target.selectionStart !== target.selectionEnd
    ) {
        return true;
    }
    return Boolean(window.getSelection()?.toString());
}

/**
 * The `onCopy` / `onPaste` pair to spread onto the Autocomplete; both events
 * bubble up from its input to the root.
 */
export function chipClipboardProps<Option>(clipboard: ChipClipboard<Option>) {
    return {
        onCopy: (event: ClipboardEvent<HTMLElement>) => {
            if (
                clipboard.value.length === 0 ||
                hasTextSelection(event.target)
            ) {
                return;
            }
            event.preventDefault();
            event.clipboardData.setData(
                'text/plain',
                chipsToClipboardText(clipboard.value, clipboard.getLabel)
            );
        },
        onPaste: (event: ClipboardEvent<HTMLElement>) => {
            if (clipboard.readOnly) {
                return;
            }
            const next = pastedChips(
                event.clipboardData.getData('text/plain'),
                clipboard
            );
            if (!next) {
                return;
            }
            event.preventDefault();
            if (next.length !== clipboard.value.length) {
                clipboard.onChange(next);
            }
        }
    };
}

/**
 * The same as a hook, for a component whose handlers reach into a ref (a grid
 * cell's `apiRef`): handed to a plain function, the React Compiler cannot tell
 * that the ref is only read when the event fires.
 */
export function useChipClipboard<Option>(clipboard: ChipClipboard<Option>) {
    return chipClipboardProps(clipboard);
}
